import { Badge, Box, Button, Checkbox, DrawerBody, DrawerFooter, Flex, FormControl, FormHelperText, FormLabel, IconButton, Input, Link, Radio, RadioGroup, Select, Spacer, Spinner, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, Tag, Text, useToast } from "@chakra-ui/react";
import { AuthContext } from "contexts/AuthContext";
import { create, createBroadcastRecord, getAll, save, updateMany } from "lib/firebase";
import { computeDuration, planBroadcastUpdates } from "lib/broadcast";
import { asKind, isCompositeBroadcast } from "lib/playlist";
import { useContent } from "hooks/useContent";
import { useBroadcast } from "hooks/useBroadcast";
import CopyFrom from "components/selection/CopyFrom";
import ResolutionPicker from "components/resolution/ResolutionPicker";
import { useContext, useEffect, useRef, useState } from "react";
import { SingleDatepicker } from "chakra-dayzed-datepicker";
import moment from "moment";
import { parseScheduleDate } from "lib/utils";
import ContentSelection from "components/contentSelection/contentSelection";
import { remove } from "lib/firebase";
import BroadcastSelection from "components/broadcastSelection/broadcastSelection";
import { DrawerContext } from "components/drawer";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
// The Cloud Function that applies schedules runs every 5 minutes, so finer steps would not be honoured.
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'))

// Starting point for a new schedule: the broadcast as it is right now.
const scheduleFromBroadcast = (b, composite) => ({
  type: composite ? 'composite' : 'regular',
  contentList: composite ? null : [...(b.contentList || [])],
  broadcastList: composite ? [...(b.broadcastList || [])] : null,
  duration: b.duration || 0,
})

export default function BroadcastModal({ broadcast, onChange }) {

  const { user } = useContext(AuthContext)

  const { closeDrawer } = useContext(DrawerContext)

  // All content, for the list of known resolutions of the resolution picker
  const [allContent] = useContent({ user })
  // All broadcasts, for "Copy from"
  const [allBroadcasts] = useBroadcast({ user })

  const [tabIndex, setTabIndex] = useState(0)
  // The schedule starts from the broadcast's current state, but only once: later
  // visits to the tab keep whatever the user did there (even an emptied list).
  const schedulePrefilled = useRef(false)

  const handleTabsChange = (index) => {
    setTabIndex(index)
    // First visit to the Schedule tab without a schedule: offer the broadcast's
    // current state (what is in the Content tab right now) as the starting point.
    if (index === 1 && !broadcast?.schedule && !schedulePrefilled.current) {
      schedulePrefilled.current = true
      setScheduleType(type)
      setData(current => ({ ...current, schedule: scheduleFromBroadcast(current, type === 'composite') }))
    }
  }

  const [data, setData] = useState({})
  const [type, setType] = useState('regular')
  // Switching the schedule type: the broadcast's own type starts from its current
  // state again, the other type starts empty.
  const handleScheduleTypeChange = (scheduleKind) => {
    schedulePrefilled.current = true
    setScheduleType(scheduleKind)
    const composite = scheduleKind === 'composite'
    setData(current => ({
      ...current,
      schedule: scheduleKind === type
        ? scheduleFromBroadcast(current, composite)
        : { type: scheduleKind, contentList: composite ? null : [], broadcastList: composite ? [] : null, duration: 0 }
    }))
  }

  // Default for a new schedule: the next full hour, in the browser's time zone.
  const [scheduleDate, setScheduleDate] = useState(() => moment().add(1, 'hour').startOf('hour').format('YYYY-MM-DD'))
  const [scheduleHour, setScheduleHour] = useState(() => moment().add(1, 'hour').startOf('hour').format('HH'))
  const [scheduleMinute, setScheduleMinute] = useState('00')
  const [scheduleType, setScheduleType] = useState('regular')

  const toast = useToast()

  useEffect(() => {
    if (broadcast && broadcast.id !== data.id) {
      setData(broadcast)
      // `type` decides: a regular broadcast without content has no list at all in the database
      setType(isCompositeBroadcast(broadcast) ? 'composite' : 'regular')
      if (broadcast.schedule) {
        // Stored as a UTC instant; show it in the browser's time zone
        const when = parseScheduleDate(broadcast.schedule.date)
        if (when) {
          setScheduleDate(when.format('YYYY-MM-DD'))
          setScheduleHour(when.format('HH'))
          setScheduleMinute(String(Math.floor(when.minute() / 5) * 5).padStart(2, '0'))
        }
        setScheduleType(isCompositeBroadcast(broadcast.schedule) ? 'composite' : 'regular')
      } else {
        setScheduleType(isCompositeBroadcast(broadcast) ? 'composite' : 'regular')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broadcast])

  // Fresh data for everything a broadcast depends on. The lists held in the app state
  // are loaded once and cached, so they can be stale, and durations must be exact.
  const loadReferenceData = async () => {
    const [allBroadcasts, contentItems] = await Promise.all([
      getAll('broadcast', user.clientId),
      getAll('content', user.clientId),
    ])
    return { broadcasts: allBroadcasts, contentItems }
  }

  // "Copy from": takes the list of another broadcast of the SAME kind (the picker offers no other).
  // Only the form changes; the duration is calculated again from fresh data when it is saved.
  const copyFromBroadcast = (source) => {
    if (type === 'composite') {
      setData(current => ({ ...current, type: 'composite', broadcastList: [...(source.broadcastList || [])], contentList: null, duration: source.duration || 0 }))
    } else {
      setData(current => ({ ...current, type: 'regular', contentList: [...(source.contentList || [])], broadcastList: null, resolutions: source.resolutions || null, duration: source.duration || 0 }))
    }
  }

  // "Clear all": an empty list (the resolutions of a regular broadcast stay as they are)
  const clearList = () => {
    if (type === 'composite') {
      setData(current => ({ ...current, type: 'composite', broadcastList: [], contentList: null, duration: 0 }))
    } else {
      setData(current => ({ ...current, type: 'regular', contentList: [], broadcastList: null, duration: 0 }))
    }
  }

  const updateBroadcast = async () => {
    try {
      // `schedule` is attached only for display (see useBroadcast); it is not part of the record
      const { schedule, ...rest } = data
      // The chosen kind is always written, also for a regular broadcast with no content (it has
      // no list in the database, and without a type it would be taken for a composite one)
      const record = asKind({ ...rest, id: broadcast.id, updated_at: moment().toISOString() }, type)

      // The edited broadcast and everything that depends on it (multiplier copies,
      // composite broadcasts) are saved in ONE atomic write, so they cannot end up
      // out of step with each other if something fails halfway.
      const { broadcasts, contentItems } = await loadReferenceData()
      const plan = planBroadcastUpdates({ broadcasts, contentItems, edited: record, now: record.updated_at })
      await updateMany({ ...plan.updates, [`broadcast/${broadcast.id}`]: plan.edited })

      onChange && onChange()
      closeDrawer()
      toast({ title: 'Broadcast updated', status: 'success', duration: null, isClosable: true })
    } catch (err) {
      toast({ title: 'Error!', description: err.message || err, status: 'error', duration: null, isClosable: true })
    }
  }

  const updateSchedule = async () => {
    // Local date + time (browser time zone) -> one UTC instant, e.g. 10:00 in
    // Serbia (CEST) is stored as 08:00Z. The Cloud Function compares it with the
    // current time, so it fires at the same moment wherever the admin sits.
    const when = moment(`${scheduleDate} ${scheduleHour}:${scheduleMinute}`, 'YYYY-MM-DD HH:mm', true)
    if (!when.isValid()) {
      toast({ title: 'Error!', description: 'Please choose a valid schedule date and time.', status: 'error', duration: null, isClosable: true })
      return
    }

    if (!data.schedule) {
      toast({ title: 'Error!', description: 'Open the Schedule tab and choose the content first.', status: 'error', duration: null, isClosable: true })
      return
    }

    const schedule = { ...data }
    schedule.broadcastId = broadcast.id
    schedule.clientId = user.clientId
    schedule.date = when.toISOString()
    schedule.type = data.schedule.type
    // Always a real true/false: the Cloud Function copies it into the broadcast, and a
    // missing value there would make the database reject the write
    schedule.showChyron = !!data.showChyron
    schedule.contentList = data.schedule.contentList || null
    schedule.broadcastList = data.schedule.broadcastList || null
    schedule.duration = data.schedule.duration || 0

    // An empty schedule would blank the broadcast when it is applied
    if (!(schedule.contentList?.length > 0) && !(schedule.broadcastList?.length > 0)) {
      toast({ title: 'Error!', description: 'The schedule has no content. Add at least one item.', status: 'error', duration: null, isClosable: true })
      return
    }
    delete schedule.schedule
    delete schedule.name

    try {
      // Total seconds of the scheduled list, from the current content data
      const duration = computeDuration(schedule, await loadReferenceData())
      if (duration !== null) {
        schedule.duration = duration
      }

      if (data.id) {
        await save('scheduledBroadcastUpdate', schedule.id, schedule)
      } else {
        await create('scheduledBroadcastUpdate', schedule)
      }
      onChange && onChange()
      closeDrawer()
      toast({ title: 'Schedule saved', status: 'success', duration: null, isClosable: true })
    } catch (err) {
      toast({ title: 'Error!', description: err.message || err, status: 'error', duration: null, isClosable: true })
    }
  }

  const removeSchedule = async (id) => {
    if (!broadcast.schedule.id) {
      return
    }
    try {
      await remove('scheduledBroadcastUpdate', broadcast.schedule.id)

      onChange && onChange()
      closeDrawer()
      toast({ title: 'Schedule removed', status: 'success', duration: null, isClosable: true })
    } catch (err) {
      toast({ title: 'Error!', description: err.message || err, status: 'error', duration: null, isClosable: true })
    }
  }

  const createBroadcast = async () => {
    try {
      const { schedule, ...rest } = data
      const record = asKind({ ...rest, clientId: user.clientId, updated_at: moment().toISOString() }, type)

      // Total seconds are recalculated from the current content data, not taken from
      // what the form remembers
      await createBroadcastRecord(record)

      onChange && onChange()

      closeDrawer()
      toast({ title: 'Broadcast created', status: 'success', duration: null, isClosable: true })
    } catch (err) {
      toast({ title: 'Error!', description: err.message || err, status: 'error', duration: null, isClosable: true })
    }
  }

  if (!broadcast && !type) {
    return <Stack justifyContent={'center'} alignItems={'center'} pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Spinner size='xl' />
    </Stack>
  }

  return (
    <>
      <DrawerBody>
        <Stack direction='column' spacing={4}>
          <FormControl w={{ base: '100%', md: '50%' }} pr={{ base: 0, md: 4 }}>
            <FormLabel>Type</FormLabel>
            <RadioGroup onChange={(type) => setType(type)} value={type}>
              <Stack direction='row' spacing={4}>
                <Radio value='regular'>Regular broadcast</Radio>
                <Radio value='composite'>Composite broadcast</Radio>
              </Stack>
            </RadioGroup>
          </FormControl>
          <FormControl>
            <FormLabel>ID</FormLabel>
            <Input type='text' value={data.id || 'ID will be auto-generated'} disabled />
          </FormControl>
          <Stack direction={{ base: 'column', md: 'row' }} spacing={{ base: 4, md: 8 }}>
            <FormControl w={{ base: '100%', md: '50%' }}>
              <FormLabel>Name</FormLabel>
              <Input type='text' value={data.name || broadcast?.name} onChange={e => setData({...data, name: e.target.value })} />
            </FormControl>
            <FormControl w={{ base: '100%', md: '50%' }}>
              <FormLabel>Chyron</FormLabel>
              <Checkbox isChecked={data.showChyron} onChange={e => setData({...data, showChyron: e.target.checked })}>Show chyron</Checkbox>
            </FormControl>
          </Stack>
          <Tabs colorScheme='brand' variant='enclosed' index={tabIndex} onChange={handleTabsChange}>
            <TabList borderColor={'#f0f6ff'}>
              <Tab border={'none'} _focus={{ boxShadow: "none" }} _selected={{ background: '#f0f6ff'}} _hover={{background: '#fafcff'}}>{type === 'composite' ? 'Broadcasts' : 'Content'}</Tab>
              {broadcast && <Tab border={'none'} _focus={{ boxShadow: "none" }} _selected={{ background: '#f0f6ff'}} _hover={{background: '#fafcff'}}>Schedule</Tab>}
            </TabList>
            <TabPanels>
              <TabPanel>
                <CopyFrom
                  composite={type === 'composite'}
                  broadcasts={allBroadcasts}
                  exceptId={broadcast?.id}
                  isEmpty={type === 'composite' ? !(data.broadcastList?.length > 0) : !(data.contentList?.length > 0)}
                  onCopy={copyFromBroadcast}
                  onClear={clearList}
                />
                {type === 'composite' ? (
                  <BroadcastSelection selected={data.broadcastList || []} onChange={({items, duration}) => setData({...data, type: 'composite', broadcastList: items, contentList: null, duration: duration})} />
                ) : (
                  <Stack direction='column' spacing={4}>
                    <FormControl>
                      <FormLabel>Resolutions</FormLabel>
                      <ResolutionPicker value={data.resolutions} content={allContent} onChange={resolutions => setData({...data, resolutions})} />
                    </FormControl>
                    <ContentSelection resolutions={data.resolutions} selected={data.contentList || []} onChange={({items, duration}) => setData(current => ({...current, type: 'regular', contentList: items, broadcastList: null, duration: duration}))} />
                  </Stack>
                )}
              </TabPanel>
              <TabPanel>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={{ base: 4, md: 8 }} alignItems={'start'} py={4}>
                  <FormControl w={{ base: '100%', md: '50%' }} pr={{ base: 0, md: 4 }}>
                    <FormLabel>Type</FormLabel>
                    <RadioGroup onChange={handleScheduleTypeChange} value={scheduleType}>
                      <Stack direction='row' spacing={4}>
                        <Radio value='regular'>Regular broadcast</Radio>
                        <Radio value='composite'>Composite broadcast</Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                  <FormControl w={{ base: '100%', md: '50%' }} pr={{ base: 0, md: 4 }}>
                    <FormLabel>Schedule for</FormLabel>
                    <Stack direction='row' spacing={2} alignItems='center' wrap='wrap'>
                      <Box flex='1' minW='160px'>
                        <SingleDatepicker
                          name="date-input"
                          date={moment(scheduleDate).toDate()}
                          onDateChange={date => setScheduleDate(moment(date).format('YYYY-MM-DD'))}
                        />
                      </Box>
                      <Select w='80px' aria-label='Hour' value={scheduleHour} onChange={e => setScheduleHour(e.target.value)}>
                        {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                      </Select>
                      <Text>:</Text>
                      <Select w='80px' aria-label='Minute' value={scheduleMinute} onChange={e => setScheduleMinute(e.target.value)}>
                        {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
                      </Select>
                    </Stack>
                    <FormHelperText>
                      Time zone: {Intl.DateTimeFormat().resolvedOptions().timeZone} (UTC{moment().format('Z')}). The change is applied within 5 minutes after this time.
                    </FormHelperText>
                  </FormControl>
                </Stack>
                {scheduleType === 'composite' ? (
                  <BroadcastSelection selected={data.schedule?.broadcastList || []} onChange={({items, duration}) => setData({...data, schedule: { ...data.schedule, type: 'composite', broadcastList: items, contentList: null, duration: duration}})} />
                ) : (
                  <ContentSelection resolutions={data.resolutions} selected={data.schedule?.contentList || []} onChange={({items, duration}) => setData({...data, schedule: {...data.schedule, type: 'regular', contentList: items, broadcastList: null, duration: duration}})} />
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
          
        </Stack>
      </DrawerBody>
      <DrawerFooter>
        {tabIndex === 0 && (
          <Box>
            {!broadcast ? (
              <Button colorScheme='brand' borderRadius="30px" me="10px" onClick={() => createBroadcast()}>
                Create broadcast
              </Button>
            ) : (
              <Button colorScheme='brand' borderRadius="30px" me="10px" onClick={() => updateBroadcast()}>
                Save broadcast
              </Button>
            )}
          </Box>
        )}
        {tabIndex === 1 && (
          <Box>
            {broadcast?.schedule ? (
              <Flex>
                <Button colorScheme='red' borderRadius="30px" me="10px" onClick={() => removeSchedule()}>
                  Remove schedule
                </Button>
                <Button colorScheme='brand' borderRadius="30px" me="10px" onClick={() => updateSchedule()}>
                  Save schedule
                </Button>
              </Flex>
            ) : (
              <Button colorScheme='brand' borderRadius="30px" me="10px" onClick={() => updateSchedule()}>
                Add schedule
              </Button>
            )}
          </Box>
        )}
      </DrawerFooter>
    </>
  );
}